/**
 * Global application logger.
 *
 * Reads VITE_LOG_LEVEL from the environment (set in .env.development / .env.production).
 * Defaults to 'info' in dev builds and 'warn' in production builds.
 *
 * Log level hierarchy (most → least verbose):
 *   trace → debug → info → warn → error
 *
 * Usage:
 *   import logger from '@/lib/logger';
 *   logger.trace('raw USB frame bytes');
 *   logger.debug('something detailed');
 *   logger.info('connected');
 *   logger.warn('slow response');
 *   logger.error('failed!', error);
 *
 * Runtime override from the browser console:
 *   __setLogLevel('trace')   // enable all output including wire data
 *   __setLogLevel('debug')   // detailed app diagnostics
 *   __setLogLevel('warn')    // warnings + errors only
 *   __getLogLevel()          // show the current level
 */

export type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'trace';

const LEVEL_ORDER: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
  trace: 4,
};

function resolveConfiguredLevel(): LogLevel {
  const raw = (import.meta.env.VITE_LOG_LEVEL ?? '') as string;
  if (raw in LEVEL_ORDER) return raw as LogLevel;
  return import.meta.env.DEV ? 'info' : 'warn';
}

let currentLevel: LogLevel = resolveConfiguredLevel();

/** Returns true if a given level passes the currently configured threshold. */
export function isLogLevelEnabled(level: string): boolean {
  const order = LEVEL_ORDER[level as LogLevel] ?? LEVEL_ORDER.debug;
  return order <= LEVEL_ORDER[currentLevel];
}

export function setLogLevel(level: LogLevel): void {
  currentLevel = level;
}

export function getLogLevel(): LogLevel {
  return currentLevel;
}

const logger = {
  trace: (...args: unknown[]): void => {
    if (isLogLevelEnabled('trace')) console.debug(...args);
  },
  debug: (...args: unknown[]): void => {
    if (isLogLevelEnabled('debug')) console.debug(...args);
  },
  info: (...args: unknown[]): void => {
    if (isLogLevelEnabled('info')) console.info(...args);
  },
  warn: (...args: unknown[]): void => {
    if (isLogLevelEnabled('warn')) console.warn(...args);
  },
  /** Errors always output regardless of configured level. */
  error: (...args: unknown[]): void => {
    console.error(...args);
  },
};

// Expose runtime level control in the browser console.
if (typeof window !== 'undefined') {
  (window as any).__setLogLevel = (level: string) => {
    if (level in LEVEL_ORDER) {
      setLogLevel(level as LogLevel);
      console.info(`[Logger] Log level set to '${level}'`);
    } else {
      console.warn(`[Logger] Unknown level '${level}'. Valid: ${Object.keys(LEVEL_ORDER).join(', ')}`);
    }
  };
  (window as any).__getLogLevel = () => {
    console.info(`[Logger] Current log level: '${currentLevel}'`);
    return currentLevel;
  };
}

export default logger;
